import { useId, type ReactNode } from "react";

import { cx } from "@/lib/cx";

/**
 * Label, control, and one line of hint or error underneath.
 *
 * This layout was private to `CardEditor`, so every other hint and error in
 * the app was positioned by hand — which is why the same "optional" note sat
 * at three different distances from three different fields, and why several
 * errors were never wired to their control with `aria-describedby` at all.
 *
 * Two ways to use it, and the second is the one that matters:
 *
 * ```tsx
 * // A control that owns its own id (rare).
 * <FormField label="Title" htmlFor="title"><input id="title" /></FormField>
 *
 * // The normal case: the field mints the ids and hands them over, so the
 * // wiring cannot be forgotten or written differently at the next call site.
 * <FormField label="Lane" as="group" hint="Where the card sits on the board">
 *   {({ describedBy }) => <Segmented label="Lane" aria-describedby={describedBy} … />}
 * </FormField>
 * ```
 *
 * `as="group"` exists because a `<label>` may only point at one form control:
 * a segmented control, a chip row or a pair of number inputs needs a caption
 * and a `role="group"`, not a label that silently focuses the first radio.
 */

export type FormFieldRenderProps = {
  /** Put this on the control. */
  id: string;
  /** Put this on the control — it points at whichever of hint/error is live. */
  describedBy: string | undefined;
  /** True when `error` is set; the control should mirror it in `aria-invalid`. */
  invalid: boolean;
};

export type FormFieldProps = {
  label: ReactNode;
  children: ReactNode | ((props: FormFieldRenderProps) => ReactNode);
  /** Quiet guidance. Replaced by `error` when there is one. */
  hint?: ReactNode;
  /** The reason the value is not acceptable. */
  error?: ReactNode;
  /** Use when the control already has a stable id of its own. */
  htmlFor?: string;
  /** Renders the `*` and is expected to be mirrored on the control. */
  required?: boolean;
  /** `label` for a single control, `group` for a composite one. */
  as?: "label" | "group";
  className?: string;
};

export function FormField({
  label,
  children,
  hint,
  error,
  htmlFor,
  required = false,
  as = "label",
  className,
}: FormFieldProps) {
  const generated = useId();
  const id = htmlFor ?? `${generated}-control`;
  const hintId = `${generated}-hint`;
  const errorId = `${generated}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  const rendered =
    typeof children === "function"
      ? children({ id, describedBy, invalid: Boolean(error) })
      : children;

  const caption = (
    <>
      {label}
      {required ? (
        <span aria-hidden className="text-danger">
          {" "}
          *
        </span>
      ) : null}
    </>
  );

  return (
    <div
      role={as === "group" ? "group" : undefined}
      aria-labelledby={as === "group" ? `${generated}-label` : undefined}
      className={cx("flex flex-col gap-2", className)}
    >
      {/* The caption is `text-label` (13/500) rather than the eyebrow's
          uppercase `text-caption`: iOS reserves the uppercase step for a *group*
          header above a whole inset list, and using it on every single field
          turns a form into a wall of shouting. */}
      {as === "group" ? (
        <span id={`${generated}-label`} className="text-label font-medium text-muted">
          {caption}
        </span>
      ) : (
        <label htmlFor={id} className="text-label font-medium text-muted">
          {caption}
        </label>
      )}

      {rendered}

      {/* Hints and errors are second lines, so both use the compact caption
          step. The error keeps its semantic colour and live-region role. */}
      {error ? (
        <p id={errorId} role="alert" className="text-caption text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-caption text-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
